export type ReleaseNoteCategory = 'New' | 'Improved' | 'Fixed';

export type ReleaseNoteItem = {
  category: ReleaseNoteCategory;
  title: string;
  description: string;
  audience?: string;
};

export type ReleaseNote = {
  version: string;
  date: string;
  headline: string;
  summary: string;
  highlights: string[];
  items: ReleaseNoteItem[];
};

export const releaseNotes: ReleaseNote[] = [
  {
    version: 'Mar 2026',
    date: 'March 2026',
    headline: 'Analytics, cancellation tracking, delay reasons, and staffing insights',
    summary:
      'This release expands executive and group analytics, improves OR Schedule reporting, and adds new workflow controls for cancellations, delays, physician-out timestamps, and staffing analysis.',
    highlights: ['Executive analytics', 'OR Schedule reporting', 'Delay and cancellation reasons'],
    items: [
      {
        category: 'New',
        title: 'Group Performance Summary',
        audience: 'Group Admins',
        description:
          'Group admins can view a high-level performance summary across the centers they manage, including side-by-side comparison between locations.',
      },
      {
        category: 'New',
        title: 'Executive Analytics',
        audience: 'Administrators, Group Admins',
        description:
          'A new Executive tab focuses on key performance indicators including first case on-time starts, turnover time, OR utilization, case volume, last patient out of OR delay, and registration-to-OR entry. The page supports date filtering, trend comparison, and configurable target values.',
      },
      {
        category: 'New',
        title: 'OR Schedule Analytics V1.1',
        audience: 'Admins',
        description:
          'The OR Schedule analytics tab now visualizes daily blocks, turnover times, schedule accuracy, on-time starts, room and physician filters, date filters, adjustable time windows, and hover details comparing actual timestamps to scheduled times.',
      },
      {
        category: 'New',
        title: 'Automated OR Schedule Reports',
        audience: 'Admins',
        description:
          'OR Schedule Analytics reports can be sent automatically after the last patient leaves the OR, with an optional fallback report hour if the automatic report is not triggered.',
      },
      {
        category: 'New',
        title: 'Physician Out iPad Button',
        audience: 'Admins, Physicians, OR Nurses',
        description:
          'OR iPads now include a control to mark when the physician leaves the room. When physician-in and physician-out are captured, OR Schedule analytics can show those milestones with arrow indicators.',
      },
      {
        category: 'New',
        title: 'Cancellation Reasons',
        audience: 'Admins, Front Desk, PreOP Nurse',
        description:
          'Cancellation reasons can be captured when canceling procedures from the OR Schedule or PreOP iPad. The collected reasons appear in Analytics under General > Cancellation Reasons, including counts by selected time period.',
      },
      {
        category: 'New',
        title: 'Delay Reasons on the OR iPad',
        audience: 'OR Nurse',
        description:
          'Delay reasons can require nurses to select a configured reason before moving a delayed procedure forward on the OR iPad. A grace period can be configured, and the feature is disabled by default until requested.',
      },
      {
        category: 'Improved',
        title: 'Staff Sensing Analytics',
        audience: 'Admin',
        description:
          'Staff analytics now include filtering by roles, departments, and specific staff members, plus a chart toggle between current hours and staff cost. Departments can be configured and assigned from staff member profiles.',
      },
    ],
  },
  {
    version: 'Jan 2026',
    date: 'January 2026',
    headline: 'Operational flow analytics and patient journey editing',
    summary:
      'This release introduces a beta operational flow dashboard, adds discharge-date editing from the patient profile, and includes platform performance and stability improvements.',
    highlights: ['OR Schedule Analytics beta', 'Discharge date editing', 'Performance and stability'],
    items: [
      {
        category: 'New',
        title: 'OR Schedule Analytics - Operational Flow beta',
        audience: 'Admin, Manager',
        description:
          'A new analytics module provides a consolidated view of operational flow, including scheduled versus actual case duration, turnover times, first case on-time performance, OR utilization, total surgery minutes, and more. It is available from Analytics > OR Schedule.',
      },
      {
        category: 'Improved',
        title: 'Patient Journey discharge date editing',
        audience: 'All Users',
        description:
          'Users can edit a patient’s discharge date directly from the patient profile. This helps correct missed discharges, prevents inaccurate overnight stays, and keeps operational reporting data accurate.',
      },
      {
        category: 'Improved',
        title: 'Platform performance and stability',
        audience: 'All Users',
        description:
          'Behind-the-scenes platform improvements enhance performance, reduce load times, and provide a smoother experience during high-volume periods.',
      },
    ],
  },
  {
    version: 'Aug 2025',
    date: 'August 2025',
    headline: 'Recovery, room visibility, and front desk workflow improvements',
    summary:
      'This release adds new PACU and OR iPad controls, improves internal screen visibility, enhances surgeon notification behavior, and gives front desk users better PDF access.',
    highlights: ['Move to PostOP', 'Jump Recovery', 'Schedule and staffing PDFs'],
    items: [
      {
        category: 'New',
        title: 'Manual Move to PostOP',
        audience: 'PACU Staff',
        description:
          'PACU iPads now include a Move to PostOP button, allowing staff to track PostOP time without physically moving the patient to another location. This supports workflows where phase 1 and phase 2 recovery occur in the same place.',
      },
      {
        category: 'New',
        title: 'Jump Recovery',
        audience: 'OR Nurse',
        description:
          'OR iPads can move a patient back into the room with timestamps preserved when the patient was incorrectly transitioned from OR to PACU. A five-minute window is available before the PACU transition is finalized. This feature is disabled by default and can be enabled on request.',
      },
      {
        category: 'Improved',
        title: 'Internal Screen OR entry visibility',
        audience: 'All Users',
        description:
          'Internal Screens for centers with four ORs or fewer now show Entered OR Time, improving visibility into patient movement in and out of the OR.',
      },
      {
        category: 'Improved',
        title: 'Ready for Surgery surgeon notification',
        audience: 'OR Nurse',
        description:
          'Surgeon notifications from OR iPads now work without requiring the surgeon’s phone number and display a Ready for Surgery tag on the Internal Screen and Schedule Screen.',
      },
      {
        category: 'Improved',
        title: 'Flip Room timer',
        audience: 'All Users',
        description:
          'The Internal Screen now shows elapsed time on the Flip Room tag, giving staff clearer visibility into how long the tag has been active. Flip Room is controlled from the OR iPad and can be enabled by request.',
      },
      {
        category: 'Improved',
        title: 'Orthopedics timestamp editing from Patients list',
        audience: 'Admins, PACU Nurse',
        description:
          'Out of Bed and Transfer to PT timestamps can be edited directly from the Patients list through the Patient Journey, improving analytics accuracy. Orthopedic timestamps can be enabled by request.',
      },
      {
        category: 'Improved',
        title: 'Front Desk PDF access',
        audience: 'Front Desk User',
        description:
          'Front desk users can now view and print Schedule and Staffing PDFs from the OR Schedule Screen. Various pre-made PDFs are disabled by default and can be reviewed or enabled on request.',
      },
    ],
  },
];
